import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, authApi } from '@/services/api';
import { CreditCard, Plus, Edit2, Trash2, Loader2, AlertCircle, CheckCircle, ChevronDown, BookOpen } from 'lucide-react';
import BankLogo from '@/components/common/BankLogo';
import BankLedgerModal from '@/components/BankLedgerModal';
import { getBanksForCountry, resolveBankLogo } from '@/constants/pakistanBanks';

interface BankAccount {
  name: string;
  bank_name: string;
  account_title: string;
  account_number?: string;
  current_balance: number;
  is_default: number;
  logo?: string | null;
}

export default function BanksTab() {
  const qc = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [isBankDropdownOpen, setIsBankDropdownOpen] = useState(false);
  const [isCustomBank, setIsCustomBank] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
  });
  const countryCode = profile?.country || 'PK';
  const countryBanks = useMemo(() => getBanksForCountry(countryCode), [countryCode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsBankDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [form, setForm] = useState({
    bank_name: '',
    account_title: '',
    account_number: '',
    is_default: false,
  });

  const [errorMsg, setErrorMsg] = useState('');
  const [ledgerBank, setLedgerBank] = useState<BankAccount | null>(null);

  const { data: banks = [], isLoading } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message as BankAccount[];
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      bank_name: string;
      account_title: string;
      account_number: string;
      is_default: boolean;
    }) => posApi.createBankAccount(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to create bank');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: {
      name: string;
      bank_name: string;
      account_title: string;
      account_number: string;
      is_default: boolean;
    }) => posApi.updateBankAccount(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to update bank');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (name: string) => posApi.deleteBankAccount(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bankAccounts'] });
    },
    onError: (err: any) => {
      alert(err.response?.data?.message || err.message || 'Cannot delete bank. It might be linked to existing payments.');
    },
  });

  const resetForm = () => {
    setForm({ bank_name: '', account_title: '', account_number: '', is_default: false });
    setEditing(null);
    setErrorMsg('');
    setIsCustomBank(false);
  };

  const handleOpenAdd = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (bank: BankAccount) => {
    setEditing(bank);
    setForm({
      bank_name: bank.bank_name,
      account_title: bank.account_title || '',
      account_number: bank.account_number || '',
      is_default: bank.is_default === 1,
    });
    setIsCustomBank(!countryBanks.some(b => b.name === bank.bank_name) && bank.bank_name !== '');
    setIsModalOpen(true);
  };

  const handleDelete = (name: string) => {
    if (window.confirm('Are you sure you want to delete this bank?')) {
      deleteMutation.mutate(name);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.bank_name.trim()) {
      setErrorMsg('Bank name is required');
      return;
    }

    const payload = {
      bank_name: form.bank_name.trim(),
      account_title: form.account_title.trim() || form.bank_name.trim(),
      account_number: form.account_number.trim(),
      is_default: form.is_default,
      logo: resolveBankLogo(form.bank_name.trim(), null, countryCode),
    };

    if (editing) {
      updateMutation.mutate({ name: editing.name, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary-600" />
              Banks
            </h2>
            <p className="text-sm text-gray-500">
              Add banks and wallets (e.g. HBL, EasyPaisa, JazzCash) for bank payments.
            </p>
          </div>
          <button onClick={handleOpenAdd} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Bank
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : banks.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No banks found. Add EasyPaisa, JazzCash, or your bank account.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="p-4 rounded-tl-xl">Bank Name</th>
                  <th className="p-4">Account Title</th>
                  <th className="p-4">Account No</th>
                  <th className="p-4 text-right rounded-tr-xl">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {banks.map((bank) => (
                  <tr key={bank.name} className="hover:bg-gray-50/50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <BankLogo
                          bankName={bank.bank_name}
                          logoUrl={resolveBankLogo(bank.bank_name, bank.logo, countryCode)}
                          className="w-8 h-8 rounded-md object-contain bg-white border border-gray-100 shadow-sm"
                          fallbackClassName="w-8 h-8 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-500 shadow-sm"
                        />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{bank.bank_name}</span>
                            {bank.is_default === 1 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-bold bg-primary-50 text-primary-700">
                                <CheckCircle className="w-3 h-3" /> Default
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-gray-600">{bank.account_title}</td>
                    <td className="p-4 text-sm text-gray-600 font-mono">{bank.account_number || '—'}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setLedgerBank(bank)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                          title="Ledger"
                        >
                          <BookOpen className="w-3.5 h-3.5" />
                          Ledger
                        </button>
                        <button
                          onClick={() => handleOpenEdit(bank)}
                          className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(bank.name)}
                          disabled={deleteMutation.isPending}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {deleteMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {editing ? 'Edit Bank' : 'Add Bank'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {errorMsg && (
                <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>{errorMsg}</p>
                </div>
              )}

              <div className="relative" ref={dropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                {!isCustomBank ? (
                  <>
                    <div 
                      className="input-field flex items-center justify-between cursor-pointer bg-white"
                      onClick={() => setIsBankDropdownOpen(!isBankDropdownOpen)}
                    >
                      <div className="flex items-center gap-2">
                        {form.bank_name ? (
                          <BankLogo
                            bankName={form.bank_name}
                            logoUrl={resolveBankLogo(form.bank_name, null, countryCode)}
                            className="w-5 h-5 rounded-sm object-contain bg-white"
                            fallbackClassName="w-5 h-5 rounded-sm bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500"
                          />
                        ) : null}
                        <span className={form.bank_name ? "text-gray-900" : "text-gray-400"}>
                          {form.bank_name || 'Select a bank...'}
                        </span>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isBankDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>

                    {isBankDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto py-1">
                        {countryBanks.map(bank => (
                          <div
                            key={bank.name}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${form.bank_name === bank.name ? 'bg-primary-50' : ''}`}
                            onClick={() => {
                              if (bank.name === 'Other') {
                                setIsCustomBank(true);
                                setForm({ ...form, bank_name: '' });
                              } else {
                                setIsCustomBank(false);
                                setForm({ ...form, bank_name: bank.name });
                              }
                              setIsBankDropdownOpen(false);
                            }}
                          >
                            <BankLogo
                              bankName={bank.name}
                              logoUrl={bank.logo}
                              className="w-6 h-6 rounded-sm object-contain bg-white border border-gray-100"
                              fallbackClassName="w-6 h-6 rounded-sm bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] font-bold text-gray-500"
                            />
                            <span className={`text-sm ${form.bank_name === bank.name ? 'font-semibold text-primary-700' : 'font-medium text-gray-700'}`}>
                              {bank.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      className="input-field flex-1"
                      placeholder="Enter custom bank name"
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomBank(false);
                        setForm({ ...form, bank_name: '' });
                      }}
                      className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors whitespace-nowrap"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Title</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Optional — defaults to bank name"
                  value={form.account_title}
                  onChange={(e) => setForm({ ...form, account_title: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  className="input-field font-mono"
                  placeholder="Optional"
                  value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                />
              </div>

              <label className="flex items-center gap-3 pt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_default}
                  onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                />
                <span className="text-sm font-medium text-gray-700">Set as default bank</span>
              </label>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-primary flex items-center gap-2"
                >
                  {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editing ? 'Save Changes' : 'Create Bank'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <BankLedgerModal
        isOpen={Boolean(ledgerBank)}
        onClose={() => setLedgerBank(null)}
        bankAccount={ledgerBank?.name || ''}
        bankLabel={
          ledgerBank
            ? ledgerBank.account_title && ledgerBank.account_title !== ledgerBank.bank_name
              ? `${ledgerBank.bank_name} — ${ledgerBank.account_title}`
              : ledgerBank.bank_name
            : ''
        }
      />
    </div>
  );
}
